import React from 'react';
import GoldButton from '@components/GoldButton/GoldButton';
import BlueOutlineButton from '@components/BlueOutlineButton/BlueOutlineButton';
import ErrorMessage from '@components/ErrorMessage/ErrorMessage';
import { Box, createStyles, makeStyles } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RequestFormValidatedState } from '@interfaces/RequestForm/forms/RequestFormValidatedState';
import RequestFormSelector from '@store/selectors/RequestFormSelector';
import { useConditionalSurgeryRequestSubmit } from '@components/RequestForm/utilities/hooks/useConditionalSurgeryRequestSubmit/useConditionalSurgeryRequestSubmit';
import { deepObjectEquality } from '@utilities/deepObjectEquality';

export const GENERIC_UI_ERROR = 'Please check the page for errors.';
export const GENERIC_SERVICE_ERROR = 'There was a problem submitting your request. Please try again later. ';
const formErrors: { [serviceError: string]: string } = {
	'409 - Duplicate surgery request':
		'We have found an existing request with the same doctor, patient, date, and time. Please review your request.',
	'422 - No primary procedure found': 'There should be a primary procedure selected.',
	[GENERIC_UI_ERROR]: GENERIC_UI_ERROR,
	[GENERIC_SERVICE_ERROR]: GENERIC_SERVICE_ERROR,
};

const useStyles = makeStyles((theme) =>
	createStyles({
		buttonContainer: {
			display: 'flex',
			justifyContent: 'flex-end',
			alignItems: 'center',
			gap: theme.spacing(2),
		},
	}),
);

interface FormSubmitSectionProps {
	saveAuthenticatedForm: (formResults: RequestFormValidatedState) => void;
	handleFormReset: () => void;
	isEdit?: boolean;
}

export const FormSubmitSection: React.FC<FormSubmitSectionProps> = ({
	saveAuthenticatedForm,
	handleFormReset,
	isEdit = false,
}) => {
	const classes = useStyles();

	const {
		handleSubmit,
		formState: { errors, isLoading },
		watch,
		clearErrors,
	} = useFormContext<RequestFormValidatedState>();
	const [submittedVales, setSubmittedValues] = React.useState<RequestFormValidatedState | null>(null);

	const currentValues = watch();
	const { formError } = useSelector(RequestFormSelector);

	const onSubmit = useConditionalSurgeryRequestSubmit({
		appointmentLocation: currentValues.appointmentLocation,
		saveAuthenticatedForm,
		attachments: currentValues.attachments,
	});

	const handleFormSubmit = React.useCallback(
		(formResults: RequestFormValidatedState) => {
			setSubmittedValues(formResults);
			onSubmit(formResults);
		},
		[onSubmit],
	);

	const hasChangedAfterSave = React.useMemo(() => {
		if (!submittedVales) {
			return false;
		}
		return !deepObjectEquality(currentValues, submittedVales);
	}, [currentValues, submittedVales]);

	const hasRhfError = errors && Object.keys(errors).length > 0;
	const userError = hasRhfError ? GENERIC_UI_ERROR : null;
	const serviceError = formError
		? formErrors[formError] || GENERIC_SERVICE_ERROR + formError
		: null;

	const shouldShowServiceError = serviceError && !hasChangedAfterSave;
	const showError = userError || shouldShowServiceError;

	React.useEffect(() => {
		if (!currentValues.appointmentLocation && hasRhfError) {
			clearErrors();
		}
	}, [clearErrors, hasRhfError, currentValues.appointmentLocation]);

	if (!currentValues.appointmentLocation) {
		return null;
	}

	return (
		<Box className={classes.buttonContainer}>
			{showError && (
				<Box>
					<ErrorMessage
						errorField={{
							message: (userError || serviceError) as string,
						}}
					/>
				</Box>
			)}
			{isEdit && (
				<BlueOutlineButton data-field="edit-request-form-cancel" onClick={handleFormReset}>
					Cancel
				</BlueOutlineButton>
			)}
			{!isEdit && (
				<BlueOutlineButton data-field="request-form-cancel" onClick={handleFormReset}>
					Reset
				</BlueOutlineButton>
			)}
			<GoldButton
				onClick={!showError ? handleSubmit(handleFormSubmit) : undefined}
				id="request-form-save"
			>
				{isLoading ? 'Loading...' : (!isEdit ? 'Submit Surgery Request' : 'Submit Revision')}
			</GoldButton>
		</Box>
	);
};
